/**
 * Landing Page Builder V2 - Constants
 * Image upload limits and Supabase Storage render-transform sizing.
 */

// ============================================================
// Image Upload
// ============================================================

/** Maximum file size for uploaded images (10MB) */
export const MAX_IMAGE_FILE_SIZE = 10 * 1024 * 1024;

/** Maximum image dimension (width or height) before compression */
export const MAX_IMAGE_DIMENSION = 4096;

/** Compression quality (0-1) for resized JPEG images (PNGs are lossless) */
export const IMAGE_COMPRESSION_QUALITY = 0.92;

// ============================================================
// Image rendering (Supabase Storage transforms)
// ============================================================
// Public pages serve photos through Supabase's `/render/image/` endpoint (see image-cdn.ts) so
// multi-MB originals download as right-sized WebP. Widths are ~2x the largest CSS display size to
// stay crisp on retina; quality 70 is visually lossless for photos at these sizes.

/** Output quality (20-100) for transformed landing-page photos. */
export const LANDING_IMAGE_RENDER_QUALITY = 70;

/** Higher quality for the full-bleed hero — it's the largest, most prominent surface. */
export const HERO_IMAGE_RENDER_QUALITY = 80;

/** Render width for the full-bleed hero background image (sized for large/retina displays). */
export const HERO_IMAGE_RENDER_WIDTH = 2560;

/** Render width for the in-page carousel slide (reused by the zoom/lightbox so it opens instantly). */
export const CAROUSEL_IMAGE_RENDER_WIDTH = 1600;

/** Render width for activity / pricing card cover photos. */
export const CARD_IMAGE_RENDER_WIDTH = 800;

/** Render width for circular team-member avatars. */
export const TEAM_IMAGE_RENDER_WIDTH = 480;

/** Render width for a generic section background image (full-bleed, similar prominence to the hero). */
export const SECTION_BG_IMAGE_RENDER_WIDTH = 2560;

/** Output quality (20-100) for a generic section background image. */
export const SECTION_BG_IMAGE_RENDER_QUALITY = 75;

/**
 * Render width for a Divider's background image. Lower than a full section's because a divider is
 * a thin strip — at most {@link MAX_DIVIDER_HEIGHT_PX} tall and usually 32-128px — so nearly all
 * of a full-size photo's vertical detail is cropped away by `background-size: cover` and paid for
 * in bandwidth regardless.
 *
 * Only the width is reduced, and only to 1920. The strip is still full-bleed, so `cover` scales it
 * by width and horizontal resolution is what stays visible; dropping it as far as the height would
 * suggest would visibly soften the image on a wide display. Cropping server-side to an actually
 * short image would be the real fix, but `transformStorageImageUrl` takes a width only and scales
 * height proportionally, so it can't ask for one.
 */
export const DIVIDER_BG_IMAGE_RENDER_WIDTH = 1920;

/** Max dimension (px) a section background image is compressed to before upload. */
export const SECTION_BG_IMAGE_MAX_DIMENSION = 2560;

// ============================================================
// Section background image scrim
// ============================================================
// A section with a background image composites a flat scrim under the photo so text stays
// legible over an arbitrary image. The scrim's tone (not just its opacity) has to track the
// section's actual text color — a white wash reads fine under dark text but erases light text on
// a dark-themed palette just as badly as no scrim at all under dark text on a dark photo. Its
// opacity is user-adjustable per section (`CardBackground`'s `overlayOpacity`, 0-100) — these
// bounds and the default reproduce the original fixed 65% wash for any section that hasn't set one.

/** Scrim tone for a section with dark (default) text — a light wash. */
export const SECTION_BG_IMAGE_SCRIM_LIGHT_HEX = "#ffffff";

/** Scrim tone for a section with light text (a dark-themed palette) — a dark wash. */
export const SECTION_BG_IMAGE_SCRIM_DARK_HEX = "#0f172a";

/** Default overlay opacity (0-100) when a section's image background doesn't set one explicitly. */
export const SECTION_BG_IMAGE_DEFAULT_OVERLAY_OPACITY = 65;

/** Lowest overlay opacity (0-100) the editor's slider allows. */
export const SECTION_BG_IMAGE_OVERLAY_OPACITY_MIN = 0;

/** Highest overlay opacity (0-100) the editor's slider allows. */
export const SECTION_BG_IMAGE_OVERLAY_OPACITY_MAX = 100;

/** Increment (percentage points) the editor's overlay-opacity slider moves in. */
export const SECTION_BG_IMAGE_OVERLAY_OPACITY_STEP = 5;

/**
 * Overlay opacity below which the editor warns that text may not stay readable. Contrast for an
 * image section is checked against the scrim's own tone, which only holds while the scrim is
 * strong enough to dominate the photo — under this it can't be, and the photo's actual colors
 * (unknowable without sampling its pixels) decide legibility.
 */
export const SECTION_BG_IMAGE_OVERLAY_OPACITY_READABLE_FLOOR = 40;

// ============================================================
// Section text colors
// ============================================================
// The light/dark candidates a section's body text is picked from when the page's own configured
// text color doesn't clear WCAG AA against the section's background. Deliberately separate
// constants from the scrim tones above, even though two of the values coincide today: retuning a
// scrim's tint must not silently retint every section's body text.

/**
 * Opacity of the hover/selected tint used by controls sitting directly on a section (filter
 * chips). Derived from the section's own text color, so it reads on a light or dark section
 * without either needing its own hardcoded pill color.
 */
export const SECTION_SURFACE_HOVER_ALPHA = 0.12;

/**
 * The surface colour of a card/tile that sits *inside* a section (pricing cards, sponsor cards,
 * card-grid cards all render `bg-white`). Content on one of these is read against the card, never
 * against the section or the page, so its accent is contrast-checked against this instead.
 */
export const SECTION_CARD_SURFACE_HEX = "#ffffff";

/** Light candidate for section body text (on a dark section background). */
export const SECTION_TEXT_LIGHT_HEX = "#ffffff";

/** Dark candidate for section body text (on a light section background). */
export const SECTION_TEXT_DARK_HEX = "#0f172a";

/**
 * Last-resort section body text color. Used only for the narrow mid-luminance band (roughly
 * 0.17-0.23 relative luminance, e.g. violet `#8b5cf6`) where neither the light nor the dark
 * candidate above clears AA on its own — pure black always does.
 */
export const SECTION_TEXT_MAX_CONTRAST_HEX = "#000000";

/**
 * How far a muted section text color (captions, list markers, lead paragraphs) is blended back
 * toward the section background, 0-1. Only applied while the blended result still clears AA;
 * otherwise the full-strength text color is used instead, so muting never costs legibility.
 */
export const SECTION_TEXT_MUTED_BLEND = 0.3;
